import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { ConfirmationDialog, type ConfirmationOptions } from ".";

type Action = () => unknown | Promise<unknown>;
type Request = {
  owner: symbol;
  options: ConfirmationOptions;
  action: Action;
  resolve: (confirmed: boolean) => void;
  running: boolean;
  ownerGone: boolean;
  trigger: HTMLElement | null;
};
type ConfirmationContextValue = {
  request: (owner: symbol, options: ConfirmationOptions, action: Action) => Promise<boolean>;
  cancel: (owner: symbol) => void;
};
const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

/** Kept above menus so a portaled confirmation survives the menu closing. */
export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  const active = useRef<Request | null>(null);
  const finish = useCallback((current: Request, confirmed: boolean) => {
    if (active.current !== current) return;
    active.current = null;
    setRequest(null);
    current.resolve(confirmed);
  }, []);
  const cancel = useCallback(
    (owner: symbol) => {
      const current = active.current;
      if (current?.owner !== owner) return;
      current.ownerGone = true;
      if (!current.running) finish(current, false);
    },
    [finish],
  );
  const ask = useCallback((owner: symbol, options: ConfirmationOptions, action: Action) => {
    // Do not queue stale destructive actions or duplicate trigger clicks.
    if (active.current) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      const current: Request = {
        owner,
        options,
        action,
        resolve,
        running: false,
        ownerGone: false,
        trigger: document.activeElement instanceof HTMLElement ? document.activeElement : null,
      };
      active.current = current;
      setRequest(current);
    });
  }, []);
  useEffect(
    () => () => {
      active.current?.resolve(false);
      active.current = null;
    },
    [],
  );

  return (
    <ConfirmationContext.Provider value={{ request: ask, cancel }}>
      {children}
      {request && (
        <ConfirmationDialog
          {...request.options}
          open
          returnFocusRef={request.options.returnFocusRef || { current: request.trigger }}
          onOpenChange={(open) => {
            if (!open) finish(request, false);
          }}
          onConfirm={async () => {
            request.running = true;
            try {
              await request.action();
              finish(request, true);
            } catch (error) {
              if (request.ownerGone) finish(request, false);
              else throw error;
            } finally {
              request.running = false;
            }
          }}
        />
      )}
    </ConfirmationContext.Provider>
  );
}

/** The action must return/await its work so pending covers the entire operation. */
export function useConfirmation(scope?: unknown) {
  const context = useContext(ConfirmationContext);
  const owner = useRef(Symbol("confirmation-owner"));
  if (!context) throw new Error("useConfirmation requires ConfirmationProvider");
  const { request, cancel } = context;
  useEffect(() => {
    const id = owner.current;
    return () => cancel(id);
  }, [cancel, scope]);
  return useCallback((options: ConfirmationOptions, action: Action = () => {}) => request(owner.current, options, action), [request]);
}
